import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/authService";
import { formatApiErrorDetail } from "@/services/api";
import { PASSWORD_RULES, PASSWORD_POLICY_MSG, passwordMeetsPolicy } from "@/lib/passwordPolicy";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordMeetsPolicy(password)) {
      setError(PASSWORD_POLICY_MSG);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {done ? (
        <div data-testid="reset-done">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-light text-success-dark">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Password updated
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Redirecting you to sign in…
          </p>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Set a new password
            </h1>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Choose a strong password that meets the rules below.
            </p>
          </div>

          {!token && (
            <div className="mb-5 rounded-lg border border-warning/30 bg-warning-light px-3 py-2.5 text-sm text-warning-dark">
              This reset link is missing its token. Please request a new one.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            {error && (
              <div className="rounded-lg border border-error/20 bg-error-light px-3 py-2.5 text-sm text-error-dark">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label="Toggle password visibility"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type={show ? "text" : "password"}
                required
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-11"
                data-testid="reset-confirm-input"
              />
            </div>
            <ul className="space-y-1 text-xs text-muted-foreground" data-testid="reset-password-rules">
              {PASSWORD_RULES.map((r) => {
                const ok = r.test(password);
                return (
                  <li key={r.key} className={ok ? "text-success-dark" : ""}>
                    {ok ? "✓" : "•"} {r.label}
                  </li>
                );
              })}
            </ul>
            <Button
              type="submit"
              className="h-11 w-full font-semibold"
              disabled={loading || !token}
              data-testid="reset-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>

          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            data-testid="back-to-login"
          >
            Back to sign in
          </Link>
        </>
      )}
    </AuthLayout>
  );
}
